import React, { PureComponent, Children, isValidElement, cloneElement, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from './MenuContext';
import Tooltip from '../tooltip';
import MenuTitle from './MenuTitle';
import MenuItem from './MenuItem';
import ExpandMore from '../icons/internals/ExpandMore';
import ExpandLess from '../icons/internals/ExpandLess';
import ChevronRight from '../icons/internals/ChevronRight';
import Collapse from '../collapse';

function getMenuItems(children) {
  const items = [];
  Children.forEach(children, item => {
    if (isValidElement(item)) {
      items.push(item);
    }
  });
  return items;
}
/**
 * SubMenu
 */
class SubMenu extends PureComponent {
  static propTypes = {
    /**
     * 类前缀
     * */
    prefixCls: PropTypes.string,
    /** 子菜单标题 */
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    /** 显示模式 */
    mode: PropTypes.oneOf(['inline', 'popup', 'parallel']),
    /** 是否自动折叠 */
    autoFold: PropTypes.bool,
    /** pop模式状态icon */
    popIcon: PropTypes.node,
    /** 展开时状态icon（pop模式下无效） */
    expandMoreIcon: PropTypes.node,
    /** 收缩时状态iocn（pop模式下无效） */
    expandLessIcon: PropTypes.node,
    /** 组件所处层数（用于确定缩进） */
    level: PropTypes.number,
    /** 菜单当前值 */
    currentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
    /** 是否紧凑显示 */
    compact: PropTypes.bool,
    /** 前缀 */
    prefix: PropTypes.node,
    /** 状态icon渲染方法 function(isOpen, mode) => ReactNode */
    iconRender: PropTypes.func,
    /** 子菜单尺寸，继承自最上层 */
    size: PropTypes.string,
  };

  static defaultProps = {
    title: '',
    prefixCls: 'ne-submenu',
    defaultIsOpen: false,
    expandMoreIcon: <ExpandMore />,
    expandLessIcon: <ExpandLess />,
    popIcon: <ChevronRight />,
    level: 1,
    compact: false,
  };

  static contextTypes = {
    nuiMenuControl: PropTypes.object,
  };

  static getDerivedStateFromProps(props, state) {
    const { defaultIsOpen, disabled, compact } = props;
    if (compact !== state.prevCompact) {
      return {
        popupIsOpen: false,
      };
    }
    if (defaultIsOpen !== state.prevDefaultIsOpen) {
      const shouldOpen = disabled ? false : defaultIsOpen;
      return {
        popupIsOpen: shouldOpen,
        isOpen: shouldOpen,
      };
    }
    return null;
  }

  constructor(props) {
    super(props);
    const { defaultIsOpen, compact } = props;
    this.state = {
      prevCompact: compact,
      prevDefaultIsOpen: defaultIsOpen,
      isOpen: defaultIsOpen,
      popupIsOpen: defaultIsOpen,
      selected: defaultIsOpen,
    };
  }

  componentDidMount() {
    const { currentValue, disabled, children, autoFold, mode } = this.props;
    const target = Array.isArray(currentValue) ? currentValue : [currentValue];
    const childrenSelected =
      currentValue == null || disabled
        ? false
        : (autoFold || mode === 'popup') && this.findValueRecrusively(children, target);
    this.setState({
      popupIsOpen: childrenSelected,
      isOpen: childrenSelected,
      selected: mode === 'popup' && childrenSelected,
    });
  }

  componentDidUpdate(prevProps) {
    const { compact, currentValue, disabled, children, mode, autoFold } = this.props;
    const target = Array.isArray(currentValue) ? currentValue : [currentValue];
    if (
      (currentValue !== prevProps.currentValue || compact !== prevProps.compact) &&
      (autoFold || mode === 'popup')
    ) {
      const childrenSelected =
        currentValue == null || disabled ? false : this.findValueRecrusively(children, target);
      if (autoFold && mode !== 'popup') {
        this.setState({
          isOpen: childrenSelected,
        });
      }
      if (mode === 'popup') {
        this.setState({
          selected: childrenSelected,
        });
      }
    }
  }

  toggle = () => {
    const { compact, disabled } = this.props;
    this.setState(state =>
      compact
        ? {
            popupIsOpen: disabled ? false : !state.popupIsOpen,
          }
        : {
            isOpen: disabled ? false : !state.isOpen,
          },
    );
  };

  open = () => {
    const { compact, disabled } = this.props;
    this.setState(
      compact
        ? {
            popupIsOpen: !disabled,
          }
        : {
            isOpen: !disabled,
          },
    );
  };

  close = () => {
    const { compact } = this.props;
    this.setState(
      compact
        ? { popupIsOpen: false }
        : {
            isOpen: false,
          },
    );
  };

  findValueRecrusively(children, target) {
    let finded = false;
    if (!children) {
      return false;
    }
    Children.forEach(children, item => {
      if (isValidElement(item) && !finded) {
        if (target.includes(item.props.value)) {
          finded = true;
        } else if (item.type.isSubMenu || item.type.isMenuItemGroup) {
          finded = this.findValueRecrusively(item.props.children, target);
        }
      }
    });
    return finded;
  }

  render() {
    const { isOpen, selected, popupIsOpen } = this.state;
    const {
      children,
      prefixCls,
      className,
      title,
      currentValue,
      itemValue,
      disabled,
      defaultIsOpen,
      autoFold,
      mode,
      popIcon,
      expandLessIcon,
      expandMoreIcon,
      level,
      close,
      compact,
      prefix,
      iconRender,
      autoIndent,
      indentBase,
      size,
      selectable,
      ...other
    } = this.props;

    const classes = classNames(
      prefixCls,
      {
        [`${prefixCls}-inline`]: mode === 'inline',
        [`${prefixCls}-popup`]: mode === 'popup',
        [`${prefixCls}-parallel`]: mode === 'parallel',
      },
      className,
    );
    const menu = getMenuItems(children).map((item, key) =>
      cloneElement(item, {
        level: mode === 'inline' ? level + 1 : undefined,
        key: item.props.value != null ? item.props.value: `subMenu-${key}`,
        close: mode === 'popup' ? this.close : close,
      }),
    );

    const submenuListCls = classNames(`${prefixCls}-list`, {
      [`${prefixCls}-list-size-${size}`]: size,
    });
    return mode === 'parallel' ? (
      <MenuItem
        prefix={prefix}
        disableTooltip
        compact={compact}
        value={itemValue}
        className={classes}
        selectable
        suffix={iconRender ? iconRender(isOpen, mode) : popIcon}
        {...other}>
        {title}
      </MenuItem>
    ) : (
      <li className={classes}>
        {mode === 'inline' ? (
          <Fragment>
            <MenuTitle
              disabled={disabled}
              selected={!disabled && selected && selectable}
              prefix={prefix}
              compact={compact}
              level={level}
              aria-haspopup
              aria-expanded={isOpen}
              role="button"
              suffix={
                iconRender ? iconRender(isOpen, mode) : isOpen ? expandLessIcon : expandMoreIcon
              }
              onClick={this.toggle}>
              {title}
            </MenuTitle>
            <Collapse in={isOpen} timeout="auto">
              <ul role="menu" {...other} className={submenuListCls}>
                {menu}
              </ul>
            </Collapse>
          </Fragment>
        ) : (
          <Tooltip
            {...other}
            isOpen={compact ? popupIsOpen : isOpen}
            autoAdjustPosition
            content={
              <MenuTitle
                disabled={disabled}
                selected={!disabled && selected && selectable}
                prefix={prefix}
                disableTooltip
                compact={compact}
                aria-haspopup
                onClick={this.toggle}
                aria-expanded={compact ? popupIsOpen : isOpen}
                suffix={iconRender ? iconRender(isOpen, mode) : popIcon}
                autoIndent={false}
                role="button">
                {title}
              </MenuTitle>
            }
            prefixCls={`${prefixCls}-popup-container`}
            position="rightTop"
            onOpen={this.open}
            onClose={this.close}>
            <ul role="menu" className={submenuListCls}>
              {menu}
            </ul>
          </Tooltip>
        )}
      </li>
    );
  }
}

const connectedSubMenu = connect(SubMenu);
connectedSubMenu.isSubMenu = true;
connectedSubMenu.displayName = 'NeatUI.SubMenu';

export default connectedSubMenu;



// WEBPACK FOOTER //
// ./src/menu/SubMenu.jsx